import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { STATUS, DISCIPLINE_LABEL, PLACEHOLDER, pickArtistField } from "@/data/artists";
import { StatusBadge } from "@/components/latir/Ui";
import { useArtworkSold } from "@/hooks/useArtworkStatus";
import SoldBadge from "@/components/latir/SoldBadge";

// Editorial artwork card. Image is protagonist; labels are sample placeholders.
export function ArtworkCard({ artist, index, priority = false }) {
  const { t, lang } = useLang();
  const dynSold = useArtworkSold(artist.slug);
  const sold = dynSold || artist.status === "vendida";
  const st = STATUS[artist.status];
  const title = pickArtistField(artist, "title", lang) || t("sample.title");
  const hasRealImage = Boolean(
    artist.image &&
    artist.image !== PLACEHOLDER &&
    !artist.image.endsWith(".svg")
  );
  return (
    <Link
      to={`/artistas/${artist.slug}`}
      className="group block"
      data-testid={`artwork-card-${artist.slug}`}
    >
      <div className={`relative overflow-hidden ${hasRealImage ? "bg-paper-raised" : "bg-paper-deep"}`}>
        <div className="aspect-[4/5] w-full overflow-hidden">
          <img
            src={artist.image || PLACEHOLDER}
            alt={`${title} — ${artist.name}. Obra presentada en LATIR por Arteconciencia`}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            className={`${hasRealImage ? "art-editorial" : "art-placeholder"} h-full w-full object-contain p-6 sm:p-8 group-hover:scale-[1.02]`}
          />
        </div>
        {index != null && (
          <span className="absolute left-4 top-4 font-ui text-[0.62rem] tracking-[0.2em] text-ink/50 tabular-nums">
            {String(index).padStart(2, "0")}
          </span>
        )}
        {sold && (
          <span className="absolute right-3 top-3" data-testid={`sold-badge-card-${artist.slug}`}>
            <SoldBadge size="sm" testId={`sold-badge-inner-${artist.slug}`} />
          </span>
        )}
        <span className="pointer-events-none absolute inset-3 border border-latir-red/0 transition-all duration-500 group-hover:border-latir-red/25" />

        {/* Discreet "Ver obra" pill — hidden on desktop by default, visible on hover; always visible on mobile for touch usability */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-4">
          <span
            className="inline-flex items-center gap-2 bg-paper-raised/92 backdrop-blur px-4 py-2 rounded-full font-ui uppercase tracking-[0.18em] text-[0.62rem] text-ink shadow-sm transition-all duration-300 opacity-100 md:opacity-0 md:group-hover:opacity-100 group-hover:bg-latir-red group-hover:text-paper-raised"
          >
            {t("artists.view")} <ArrowRight className="w-3 h-3 text-latir-red transition-colors duration-300 group-hover:text-paper-raised" strokeWidth={1.6} />
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-[1.35rem] leading-tight text-ink transition-colors duration-300 group-hover:text-latir-red">
            {artist.name || t("sample.artist")}
          </h3>
          {artist.title && artist.title !== "PENDIENTE" && (
            <p className="font-editorial italic text-[1.15rem] text-ink-soft mt-0.5">
              {artist.title}
            </p>
          )}
          <p className="label-ui mt-2">{DISCIPLINE_LABEL[artist.discipline]?.[lang] || ""}</p>
        </div>
        {sold ? (
          <SoldBadge size="sm" className="mt-1 shrink-0" testId={`sold-badge-meta-${artist.slug}`} />
        ) : (
          <StatusBadge status={artist.status} label={st[lang]} size="sm" className="mt-1 shrink-0" />
        )}
      </div>

      {/* always-visible CTA (key for mobile / no hover) */}
      <span className="mt-4 inline-flex items-center gap-2 font-ui uppercase tracking-[0.2em] text-[0.66rem] text-latir-red transition-all duration-300 group-hover:gap-3">
        {t("artists.view")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
      </span>
    </Link>
  );
}
